import { useEffect, useState } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { ShieldCheck, Save, LayoutDashboard, KeyRound, ChevronRight } from 'lucide-react';
import { invalidateCachedPermissions } from '../../hooks/usePermissions';

// Must match DashboardLayout NAV keys + backend NAV_KEYS
const NAV_LABELS = {
  uebersicht: 'Übersicht',
  wissensdatenbank: 'Wissensdatenbank',
  dokumente: 'Dokumente',
  tickets: 'Support / Tickets',
  aufgaben: 'Aufgaben',
  kalender: 'Kalender',
  systemdatenbank: 'Systemdatenbank',
  konfigurator: 'Konfigurator',
  lager: 'Lager',
  angebote: 'Anfragen / Angebote',
  bestellungen: 'Shop-Bestellungen',
  auftraege: 'Aufträge',
  dokumentenarchiv: 'Dokumentenarchiv',
  telefonassistent: 'Telefonassistent',
  kunden: 'Kunden (B2B)',
  shop: 'Shop (Kunde)',
  systempruefung: 'Systemprüfung (Kunde)',
  'meine-bestellungen': 'Meine Bestellungen (Kunde)',
  einstellungen: 'Einstellungen',
};
const FORCED = ['uebersicht', 'einstellungen'];

export default function PermissionsTab() {
  const [subTab, setSubTab] = useState('nav');

  return (
    <div data-testid="permissions-tab">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-[#9ec043]" />
        <h2 className="text-sm font-semibold text-[#32373c]">Rollen-Rechte</h2>
      </div>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button
          onClick={() => setSubTab('nav')}
          className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            subTab === 'nav' ? 'border-[#9ec043] text-[#32373c]' : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
          data-testid="perm-subtab-nav"
        >
          <LayoutDashboard className="w-3.5 h-3.5" />
          Sidebar-Sichtbarkeit
        </button>
        <button
          onClick={() => setSubTab('actions')}
          className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            subTab === 'actions' ? 'border-[#9ec043] text-[#32373c]' : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
          data-testid="perm-subtab-actions"
        >
          <KeyRound className="w-3.5 h-3.5" />
          Aktions-Matrix
        </button>
      </div>

      {subTab === 'nav' ? <NavPermissions /> : <ActionMatrix />}
    </div>
  );
}

// =====================================================
// Sub-Tab 1: Sidebar-Sichtbarkeit (Nav-Keys)
// =====================================================
function NavPermissions() {
  const [roles, setRoles] = useState([]);
  const [perm, setPerm] = useState({});
  const [allKeys, setAllKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    const [rRoles, rPerm] = await Promise.all([
      authFetch('/api/roles'),
      authFetch('/api/permissions'),
    ]);
    if (rRoles.ok) {
      const all = await rRoles.json();
      setRoles(all.filter(r => r.id !== 'admin'));
    }
    if (rPerm.ok) {
      const d = await rPerm.json();
      setPerm(d.permissions || {});
      setAllKeys(d.allKeys || []);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = (role, key) => {
    if (FORCED.includes(key)) return;
    setPerm(p => {
      const cur = new Set(p[role] || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...p, [role]: Array.from(cur) };
    });
  };

  const saveRole = async (role) => {
    setSaving(role); setMsg('');
    const navKeys = Array.from(new Set([...(perm[role] || []), ...FORCED]));
    const r = await authFetch(`/api/permissions/${role}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ navKeys }),
    });
    if (r.ok) {
      setMsg(`✓ Sidebar-Sichtbarkeit für "${role}" gespeichert`);
      invalidateCachedPermissions();
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      setMsg(d.detail || 'Fehler');
    }
    setSaving(null);
    setTimeout(() => setMsg(''), 2500);
  };

  if (loading) return <div className="text-xs text-gray-400">Lade Rechte…</div>;

  return (
    <div>
      <p className="text-xs text-gray-400 mb-4">
        Welche Sidebar-Einträge sieht jede Rolle? „Übersicht" und „Einstellungen" sind immer aktiv (Aussperrschutz). Admin sieht grundsätzlich alles.
      </p>
      {msg && <div className={`text-xs mb-3 ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`} data-testid="perm-msg">{msg}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {roles.map(r => {
          const allowed = new Set(perm[r.id] || []);
          return (
            <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`perm-role-${r.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#32373c]">{r.label}</h3>
                  <p className="text-[10px] text-gray-400 font-mono">{r.id}</p>
                </div>
                <Button onClick={() => saveRole(r.id)} disabled={saving === r.id}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-7 disabled:opacity-50" data-testid={`btn-save-perm-${r.id}`}>
                  <Save className="w-3 h-3 mr-1" /> {saving === r.id ? 'Speichern…' : 'Speichern'}
                </Button>
              </div>
              <div className="space-y-1.5">
                {allKeys.map(key => {
                  const on = allowed.has(key) || FORCED.includes(key);
                  const locked = FORCED.includes(key);
                  return (
                    <label key={key} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${on ? 'bg-[#9ec043]/5 text-[#32373c]' : 'bg-gray-50 text-gray-500'} ${locked ? 'opacity-70' : 'cursor-pointer'}`}
                      data-testid={`perm-${r.id}-${key}`}>
                      <input type="checkbox" checked={on} disabled={locked}
                        onChange={() => toggle(r.id, key)}
                        className="accent-[#9ec043]" />
                      <span className="flex-1">{NAV_LABELS[key] || key}</span>
                      {locked && <span className="text-[9px] uppercase tracking-wider text-gray-400">fix</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================
// Sub-Tab 2: Aktions-Matrix (granulare API-Permissions)
// =====================================================
function ActionMatrix() {
  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState([]);  // [{module, actions:[{key,label}]}]
  const [actions, setActions] = useState({});    // role -> [action_key]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');
  const [activeRole, setActiveRole] = useState(null);
  const [openModule, setOpenModule] = useState(null);

  const load = async () => {
    setLoading(true);
    const [rRoles, rReg, rAct] = await Promise.all([
      authFetch('/api/roles'),
      authFetch('/api/permissions/actions/registry'),
      authFetch('/api/permissions/actions'),
    ]);
    if (rRoles.ok) {
      const all = await rRoles.json();
      // Aktions-Matrix: customer wird ausgeblendet (separate Kundenlogik)
      const filtered = all.filter(r => r.id !== 'admin' && r.id !== 'customer');
      setRoles(filtered);
      if (filtered.length && !activeRole) setActiveRole(filtered[0].id);
    }
    if (rReg.ok) {
      const d = await rReg.json();
      setRegistry(d.modules || []);
      if ((d.modules || []).length && !openModule) setOpenModule(d.modules[0].module);
    }
    if (rAct.ok) {
      const d = await rAct.json();
      setActions(d.actions || {});
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (role, key) => {
    setActions(a => {
      const cur = new Set(a[role] || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...a, [role]: Array.from(cur).sort() };
    });
  };

  const toggleModule = (role, moduleActions, allOn) => {
    setActions(a => {
      const cur = new Set(a[role] || []);
      moduleActions.forEach(({ key }) => {
        if (allOn) cur.delete(key); else cur.add(key);
      });
      return { ...a, [role]: Array.from(cur).sort() };
    });
  };

  const saveRole = async (role) => {
    setSaving(role); setMsg('');
    const r = await authFetch(`/api/permissions/actions/${role}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actions: actions[role] || [] }),
    });
    if (r.ok) {
      setMsg(`✓ Aktions-Rechte für "${role}" gespeichert`);
      invalidateCachedPermissions();
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      setMsg(typeof d.detail === 'string' ? d.detail : 'Fehler beim Speichern');
    }
    setSaving(null);
    setTimeout(() => setMsg(''), 2500);
  };

  if (loading) return <div className="text-xs text-gray-400">Lade Aktions-Matrix…</div>;
  if (!roles.length) return <div className="text-xs text-gray-400">Keine Rollen mit Aktions-Matrix.</div>;

  const active = roles.find(r => r.id === activeRole) || roles[0];
  const activeSet = new Set(actions[active.id] || []);
  const totalActions = registry.reduce((n, m) => n + m.actions.length, 0);

  return (
    <div>
      <p className="text-xs text-gray-400 mb-4">
        Granulare API-Berechtigungen pro Rolle. Jede Aktion entspricht einer Backend-Schutzregel (z. B. „Lagerbestand korrigieren"). Admin hat automatisch alle Aktionen — Customer wird separat verwaltet.
      </p>
      {msg && <div className={`text-xs mb-3 ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`} data-testid="action-msg">{msg}</div>}

      {/* Rollen-Tabs */}
      <div className="flex flex-wrap gap-1 mb-4">
        {roles.map(r => {
          const cnt = (actions[r.id] || []).length;
          const isActive = r.id === activeRole;
          return (
            <button
              key={r.id}
              onClick={() => setActiveRole(r.id)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors flex items-center gap-2 ${
                isActive
                  ? 'border-[#9ec043] bg-[#9ec043]/10 text-[#32373c]'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
              }`}
              data-testid={`action-role-${r.id}`}
            >
              {r.label}
              <span className="text-[10px] font-mono text-gray-400">{cnt}/{totalActions}</span>
            </button>
          );
        })}
      </div>

      {/* Module Akkordeon für aktive Rolle */}
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`action-matrix-${active.id}`}>
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-[#32373c]">{active.label}</h3>
            <p className="text-[10px] text-gray-400 font-mono">{active.id} · {activeSet.size} Aktionen aktiv</p>
          </div>
          <Button
            onClick={() => saveRole(active.id)}
            disabled={saving === active.id}
            className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-7 disabled:opacity-50"
            data-testid={`btn-save-actions-${active.id}`}
          >
            <Save className="w-3 h-3 mr-1" /> {saving === active.id ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>

        <div className="space-y-2">
          {registry.map(mod => {
            const modKeys = mod.actions.map(a => a.key);
            const enabledInModule = modKeys.filter(k => activeSet.has(k)).length;
            const allOn = enabledInModule === modKeys.length;
            const someOn = enabledInModule > 0 && !allOn;
            const isOpen = openModule === mod.module;
            return (
              <div key={mod.module} className="border border-gray-100 rounded-lg overflow-hidden">
                <div
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100"
                  onClick={() => setOpenModule(isOpen ? null : mod.module)}
                  data-testid={`action-module-${mod.module}`}
                >
                  <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  <span className="text-xs font-semibold text-[#32373c] flex-1">{mod.module}</span>
                  <span className={`text-[10px] font-mono ${allOn ? 'text-[#9ec043]' : someOn ? 'text-amber-500' : 'text-gray-400'}`}>
                    {enabledInModule}/{modKeys.length}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleModule(active.id, mod.actions, allOn); }}
                    className="text-[10px] px-2 py-0.5 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    data-testid={`action-module-toggle-${mod.module}`}
                  >
                    {allOn ? 'Alle aus' : 'Alle an'}
                  </button>
                </div>
                {isOpen && (
                  <div className="px-3 py-2 space-y-1">
                    {mod.actions.map(a => {
                      const on = activeSet.has(a.key);
                      return (
                        <label
                          key={a.key}
                          className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer ${on ? 'bg-[#9ec043]/5' : 'bg-white hover:bg-gray-50'}`}
                          data-testid={`action-${active.id}-${a.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggle(active.id, a.key)}
                            className="accent-[#9ec043]"
                          />
                          <span className={`flex-1 ${on ? 'text-[#32373c]' : 'text-gray-500'}`}>{a.label}</span>
                          <span className="text-[10px] font-mono text-gray-300">{a.key}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
